import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';

import assertEventType from '../../../../../common/bento/lib/assertEventType';
import { logEvent } from '../../../../Analytics/analytics';
import { ApplicationType } from '../../../types';
import { MarkBeneficiaryListAsCompleteDocument } from '../graphql/mutations/markBeneficiaryListAsComplete.gql';
import { SendAllCompanyInvitationsDocument } from '../graphql/mutations/sendAllCompanyInvitations.gql';
import { GetBeneficiariesDataDocument } from '../graphql/queries/getBeneficiariesData.gql';
import {
  CreatePhysicalPersonLegalBeneficiaryDocument,
  DeletePhysicalPersonLegalBeneficiaryDocument,
} from '../pages/ManageBeneficiariesPage/components/ManageBeneficiaryElement/graphql/mutations.gql';
import { UpdatePhysicalPersonLegalBeneficiaryDocument } from '../pages/ManageBeneficiariesPage/graphql/mutations/updatePhysicalPersonLegalBeneficiary.gql';
import {
  getDeletedSupposedBeneficiaries,
  saveDeletedSupposedBeneficiaries,
} from '../pages/ManageBeneficiariesPage/storage';
import { ManageBeneficiaryViews } from '../pages/ManageBeneficiary';
import { Model } from './model';

export enum Service {
  MarkBeneficiaryListAsComplete = 'markBeneficiaryListAsComplete',
  SendAllCompanyInvitations = 'sendAllCompanyInvitations',
  SubmitBeneficiaryForm = 'submitBeneficiaryForm',
  DeleteBeneficiary = 'deleteBeneficiary',
}

const services: MachineServices<Model, Service> = {
  deleteBeneficiary: async (context, event) => {
    assertEventType(event, 'DELETE_BENEFICIARY');
    const { companyProfileId, legalBeneficiaries, supposedBeneficiaries } =
      context.getModuleData().viewer.company;
    const { beneficiaryId, view } = event.data;

    if (view === ManageBeneficiaryViews.Edit) {
      const beneficiary = legalBeneficiaries.find(
        ({ companyLegalBeneficiaryId }) =>
          companyLegalBeneficiaryId === beneficiaryId,
      );
      await apolloClient.mutate({
        awaitRefetchQueries: true,
        mutation: DeletePhysicalPersonLegalBeneficiaryDocument,
        refetchQueries: [
          {
            query: GetBeneficiariesDataDocument,
            variables: { companyProfileId, includeShares: false },
          },
        ],
        variables: {
          input: {
            companyLegalBeneficiaryId: beneficiaryId,
            companyProfileId,
          },
        },
      });

      logEvent({
        name: 'Onboarding Beneficiary List Person Deleted',
        properties: {
          newDesign: true,
        },
        usePrefix: false,
      });

      return {
        gender: beneficiary?.gender,
        name: `${beneficiary?.firstName} ${beneficiary?.lastName}`,
      };
    }

    const beneficiary = supposedBeneficiaries.find(
      ({ supposedBeneficiaryId }) => supposedBeneficiaryId === beneficiaryId,
    );
    const deletedSupposedBeneficiaries = getDeletedSupposedBeneficiaries();

    const newSet = new Set(deletedSupposedBeneficiaries).add(beneficiaryId);

    saveDeletedSupposedBeneficiaries(newSet);

    logEvent({
      name: 'Onboarding Supposed Beneficiary Deleted',
      properties: {
        newDesign: true,
      },
      usePrefix: false,
    });

    return {
      gender: beneficiary?.gender,
      name: `${beneficiary?.firstName} ${beneficiary?.lastName}`,
    };
  },
  markBeneficiaryListAsComplete: async (context, event) => {
    assertEventType(event, 'MARK_BENEFICIARY_LIST_AS_COMPLETE');
    const { applicationType, viewer } = context.getModuleData();
    const { companyProfileId } = viewer.company;

    return apolloClient.mutate({
      awaitRefetchQueries: true,
      mutation: MarkBeneficiaryListAsCompleteDocument,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: {
            companyProfileId,
            includeShares: applicationType === ApplicationType.CapitalDeposit,
          },
        },
      ],
      variables: {
        input: {
          applicants: event.data.formValues.applicants,
          id: companyProfileId,
        },
      },
    });
  },
  sendAllCompanyInvitations: async ({ getModuleData }) => {
    const { companyProfileId } = getModuleData().viewer.company;
    const { applicationType } = getModuleData();

    return apolloClient.mutate({
      awaitRefetchQueries: true,
      mutation: SendAllCompanyInvitationsDocument,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: {
            companyProfileId,
            includeShares: applicationType === ApplicationType.CapitalDeposit,
          },
        },
      ],
      variables: {
        input: { companyProfileId },
      },
    });
  },
  submitBeneficiaryForm: async (context, event) => {
    assertEventType(event, 'SUBMIT_BENEFICIARY_FORM');
    const { companyProfileId } = context.getModuleData().viewer.company;
    const companyLegalBeneficiaryId = event.data.beneficiaryId;

    if (
      companyLegalBeneficiaryId &&
      event.data.view === ManageBeneficiaryViews.Edit
    ) {
      await apolloClient.mutate({
        mutation: UpdatePhysicalPersonLegalBeneficiaryDocument,
        variables: {
          input: {
            ...event.data.formValues,
            companyLegalBeneficiaryId,
            companyProfileId,
          },
        },
      });
    } else {
      await apolloClient.mutate({
        awaitRefetchQueries: true,
        mutation: CreatePhysicalPersonLegalBeneficiaryDocument,
        refetchQueries: [
          {
            query: GetBeneficiariesDataDocument,
            variables: { companyProfileId, includeShares: false },
          },
        ],
        variables: {
          input: {
            ...event.data.formValues,
            companyProfileId,
          },
        },
      });
    }

    logEvent({
      name: 'Onboarding Beneficiary List Person Saved',
      properties: {
        newDesign: true,
      },
      usePrefix: false,
    });

    const { firstName, gender, lastName } = event.data.formValues;
    return {
      gender,
      name: `${firstName} ${lastName}`,
      view: event.data.view,
    };
  },
};

export default services;
